// Main-thread Web Audio playback of sound-bank recordings, for the two non-granular bank uses:
//   - ambient layers: the active bank loops as a continuous bed (like oceanBed, but real audio).
//   - direct sampler: a one-shot slice of the bank is triggered on a strike (bypasses the
//     resonators — raw sample playback through the oceanic reverb).
// Granular excitation (the third use) lives in the engine; this module is the non-resonator path.

const ONESHOT_SEC = 0.5; // length of a sampler hit
const ONESHOT_ATTACK = 0.006;
const ONESHOT_RELEASE = 0.09;
const AMBIENT_RAMP = 0.4; // seconds, setTargetAtTime time-constant for the ambient bed
const AMBIENT_MAX = 0.25; // ceiling for the ambient bed gain at slider=1 (it bypasses the leveler)
const ONESHOT_MAX_GAIN = 1.0; // per-hit ceiling; conservative to leave headroom in the shared bus

/**
 * Pure: start offset (seconds) for a one-shot of `sliceSec` taken from a buffer `durationSec` long,
 * given `offset01` in [0,1]. Clamped so the slice fits entirely inside the buffer.
 */
export function oneShotOffsetSec(durationSec: number, sliceSec: number, offset01: number): number {
  if (durationSec <= sliceSec) return 0;
  const max = durationSec - sliceSec;
  return Math.max(0, Math.min(1, offset01)) * max;
}

export class SamplePlayer {
  private readonly ambientGain: GainNode;
  private ambientBuf: AudioBuffer | null = null;
  private ambientSrc: AudioBufferSourceNode | null = null;
  private level = 0;

  constructor(
    private readonly ctx: AudioContext,
    ambientDest: AudioNode, // MasterChain.ambientInput (post-makeup, no reverb)
    private readonly sampleDest: AudioNode, // MasterChain.sampleInput (dry + reverb, no makeup)
  ) {
    this.ambientGain = ctx.createGain();
    this.ambientGain.gain.value = 0;
    this.ambientGain.connect(ambientDest);
  }

  /** Point the ambient bed at a bank's buffer (or null to clear). Restarts the loop if audible. */
  setAmbientBuffer(buf: AudioBuffer | null): void {
    this.ambientBuf = buf;
    if (this.level > 0) this.restartAmbient();
    else if (!buf) this.stopAmbient();
  }

  /** Ambient bed level (0..1). 0 fades it out. */
  setAmbientLevel(level: number): void {
    this.level = Math.max(0, level);
    if (this.level > 0 && this.ambientBuf && !this.ambientSrc) this.restartAmbient();
    // The bed joins the bus post-makeup (bypasses the resonator leveler), so a full-scale recording
    // would dominate at level 1. Cap it to a calibrated bed level so it sits under, not over.
    this.ambientGain.gain.setTargetAtTime(this.level * AMBIENT_MAX, this.ctx.currentTime, AMBIENT_RAMP);
  }

  private restartAmbient(): void {
    this.stopAmbient();
    if (!this.ambientBuf) return;
    const src = this.ctx.createBufferSource();
    src.buffer = this.ambientBuf;
    src.loop = true;
    src.connect(this.ambientGain);
    src.start();
    this.ambientSrc = src;
  }

  private stopAmbient(): void {
    if (!this.ambientSrc) return;
    try {
      this.ambientSrc.stop();
    } catch {
      /* already stopped */
    }
    this.ambientSrc.disconnect();
    this.ambientSrc = null;
  }

  /** Trigger a one-shot sampler hit: a windowed slice of `buf` at `offset01`, panned, into reverb. */
  playOneShot(buf: AudioBuffer, opts: { offset01?: number; gain?: number; pan?: number } = {}): void {
    const t = this.ctx.currentTime;
    const slice = Math.min(ONESHOT_SEC, buf.duration);
    const off = oneShotOffsetSec(buf.duration, slice, opts.offset01 ?? 0);
    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const g = this.ctx.createGain();
    const panner = this.ctx.createStereoPanner();
    panner.pan.value = Math.max(-1, Math.min(1, opts.pan ?? 0));
    src.connect(g);
    g.connect(panner);
    panner.connect(this.sampleDest);

    const peak = Math.max(0, Math.min(ONESHOT_MAX_GAIN, opts.gain ?? 1));
    g.gain.setValueAtTime(0, t);
    g.gain.linearRampToValueAtTime(peak, t + ONESHOT_ATTACK);
    // smooth tail so the slice doesn't click
    g.gain.setTargetAtTime(0, t + Math.max(ONESHOT_ATTACK, slice - ONESHOT_RELEASE), ONESHOT_RELEASE / 3);

    src.start(t, off, slice + 0.05);
    src.onended = () => {
      src.disconnect();
      g.disconnect();
      panner.disconnect();
    };
  }

  dispose(): void {
    this.stopAmbient();
    this.ambientGain.disconnect();
  }
}
